import PageHeading from "components/Common/PageHeading";
import React, { useState } from "react";
import { useDropzone, FileRejection } from "react-dropzone";
import upload from "../../images/icon_upload.svg";
import { DescriptionSC, DndContainerSC, SelectBtn } from "./style";
import { FixedButtonArea } from "components/Camera/components/FixedButtonArea";
import { useSelector } from "react-redux";
import { RootState } from "store/store";
import axios from "axios";
import { env_camera_url } from "serverEnv";
import LoadingModule from "components/Common/LoadingModule";

// const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_FILES_LIMIT = 20;

/**
 * ファイルサイズを人間が読みやすい形式に変換する関数（GBまで対応）
 * @param bytes - バイト数
 * @param decimals - 小数点以下の桁数（デフォルト: 2）
 * @returns 変換されたファイルサイズ文字列（例: "1.23 MB"）
 */
const formatFileSize = (bytes: number, decimals: number = 2): string => {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB"];

  const i = Math.min(
    Math.floor(Math.log(bytes) / Math.log(k)),
    sizes.length - 1
  );

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
};

export const CameraUpload = () => {
  const token = useSelector((state: RootState) => state.session.token);
  const [isUploading, setIsUploading] = useState(false);
  const [toBeUploadFiles, setTobeUploadFiles] = useState<File[]>([]);
  const onDropHandler = (files: File[]) => {
    setTobeUploadFiles(state => [...state, ...files]);
  };

  const onDropRejected = (fileRejections: FileRejection[]) => {
    const tooManyFilesError = fileRejections.find(rejection =>
      rejection.errors.some(error => error.code === "too-many-files")
    );

    const sizeOverError = fileRejections.find(rejection =>
      rejection.errors.some(error => error.code === "file-too-large")
    );

    if (tooManyFilesError) {
      alert(
        `${MAX_FILES_LIMIT}件以上のファイルをアップロードすることはできません。`
      );
    } else if (sizeOverError) {
      alert("10MB以上のファイルをアップロードすることはできません。");
    } else {
      // その他のエラー（サイズ制限など）
      alert("アップロードできないファイルが含まれています。");
    }
  };

  const { acceptedFiles, getRootProps, getInputProps } = useDropzone({
    onDrop: onDropHandler,
    onDropRejected,
    maxFiles: MAX_FILES_LIMIT,
    accept: {
      "image/*": [".png", ".jpg", ".jpeg", ".webp", ".heic", ".heif"]
    }
  });

  const files = toBeUploadFiles.map((file, index) => (
    <li key={`${file.name}-${index}`}>
      {file.name} - {formatFileSize(file.size)}
    </li>
  ));

  async function handleUpload() {
    if (toBeUploadFiles.length === 0) {
      alert("ファイルを選択してください");
      return;
    }
    setIsUploading(true);
    try {
      await Promise.all(
        toBeUploadFiles.map(file => {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('async', 'true');
          
          return axios.post(`${env_camera_url}/media-files`, formData, {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          });
        })
      );
      window.confirm("アップロードが完了しました");
      setTobeUploadFiles([]);
    } catch (error) {
      console.error(error);
    } finally {
      setIsUploading(false);
    }
  }

  return (
    <>
      {isUploading && <LoadingModule isUploadPage />}
      <div style={{ margin: "20px 20px" }}>
        <PageHeading title="アップロード" />
        <DndContainerSC {...getRootProps({ className: "dropzone" })}>
          <input {...getInputProps()} />
          <div>
            <img src={upload} alt="" />
            <DescriptionSC>
              ここにファイルをドラッグ＆ドロップ
              <br />
              または
            </DescriptionSC>
            <SelectBtn>ファイルを選択</SelectBtn>
            <DescriptionSC>
              最大{MAX_FILES_LIMIT}件までアップロードできます
              <br />
              <span>
                ※容量の大きい画像が含まれる場合は、10枚程度に枚数を減らして再度お試しください。
              </span>
            </DescriptionSC>
          </div>
        </DndContainerSC>
        <aside>
          <h4>選択したファイル</h4>
          <ul>{files}</ul>
        </aside>
      </div>
      <FixedButtonArea updateLabel="アップロード" onUpdateFunc={handleUpload} />
    </>
  );
};
